import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Checkbox from '@radix-ui/react-checkbox';
import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useForm, useFormState, Controller } from 'react-hook-form';
import MaskedInput from 'react-text-mask';
import { z } from 'zod';

import InfoTooltipSvg from '@/components/common/InfoTooltipSvg';
import LoadingSvg from '@/components/common/LoadingSvg';
import { Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverClose } from '@/components/common/popover';
import { Button } from '@/components/ui/button';
import { TmCreditCardType, TmCreditCardSelectionType, TmCreditCard, TmCreditCardRequest } from '@/tscapi/model/account';
import { TmSemafoneConfig } from '@/tscapi/model/semafone-config';
import { getSemafoneConfig } from '@/tscapi/service/semafone.service';
import { AccountUtils } from '@/tscapi/utils/account-utils';

import { usePayment } from '../Context/PaymentContext';
import { useCreditCard, useUpdateCreditCard } from '../hooks/useCreditcard';
import { FormSchemaWrapper } from '../schemas/creditcard.schema';
import { Cookie } from '../../../tscapi/utils/cookie';
declare let PCILanguage: any;
declare let PCIResetParams: any;
declare let PCIResetIframe: any;
declare let grecaptcha: any;

interface FormTscCardProps {
    selectedCreditCard?: TmCreditCard;
    setFormError?: React.Dispatch<React.SetStateAction<any>>;
}

export const FormStandardCard = ({ selectedCreditCard, setFormError }: FormTscCardProps) => {
    const isMutating = useIsMutating();

    const getExpiry = () => {
        if (selectedCreditCard && selectedCreditCard?.DisplayExpirationMonth) {
            return `${selectedCreditCard?.DisplayExpirationMonth.padStart(
                2,
                '0',
            )}/${selectedCreditCard?.DisplayExpirationYear.substring(
                selectedCreditCard?.DisplayExpirationYear.length - 2,
            )}`;
        } else {
            return '';
        }
    };
    const defaultValues = {
        semacardnumber: selectedCreditCard?.MaskedNumber || '',
        makedefault: selectedCreditCard?.IsDefault !== undefined ? selectedCreditCard?.IsDefault : false,
        semaexpirynumber: getExpiry(),
        semacvv: '',
    };
    const [creditCardType, setCreditCardType] = useState('');
    const { setMode, updateAccordionAtIndex, setShowNotification, setShowWarning } = usePayment();
    const [showSemafoneInput, setShowSemafoneInput] = useState(selectedCreditCard ? false : true);
    const [completeSemafoneResp, setCompleteSemafoneResponse] = useState('');
    const [cvvEnabled, setCvvEnabled] = useState(window.cvvCheckEnabled);
    const [formSchema, setFormSchema] = useState(
        FormSchemaWrapper(selectedCreditCard, showSemafoneInput, completeSemafoneResp, creditCardType),
    );
    type FormInput = z.infer<typeof formSchema>;
    const {
        handleSubmit,
        control,
        register,
        setError,
        setValue,
        watch,
        trigger,
        getFieldState,
        clearErrors,
        formState: { errors, isValid, isSubmitting },
    } = useForm<FormInput>({
        resolver: zodResolver(formSchema),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        criteriaMode: 'all',
        defaultValues,
    });

    const mutation = useCreditCard();
    const updateMutation = useUpdateCreditCard();

    const semaCardWatch = watch('semacardnumber', '');
    const semaExpiryWatch = watch('semaexpirynumber', '');
    const semaCvvWatch = watch('semacvv', '');

    const onNonTscCreditCardAdded = (e) => {
        setCompleteSemafoneResponse(e.target.value);
        const semafoneResp = JSON.parse(e.target.value);
        if (semafoneResp && semafoneResp.SemafoneValidationState == 'SUCCESS' && semafoneResp.Token) {
            setCreditCardType(AccountUtils.getCardTypeFromScheme(semafoneResp.CardScheme));
        }
    };

    const onEditNonTscCreditCard = (e) => {
        setShowSemafoneInput(true);
    };

    useEffect(() => {
        setFormSchema(FormSchemaWrapper(selectedCreditCard, showSemafoneInput, completeSemafoneResp, creditCardType));
    }, [selectedCreditCard, showSemafoneInput, completeSemafoneResp]);

    useEffect(() => {
        trigger('semacardnumber');
    }, [completeSemafoneResp]);

    const reCaptchaSiteKey = window.tscConfig['GoogleReCaptchaV3SiteKey'] as string;
    useEffect(() => {
        const loadReCaptcha = () => {
            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + reCaptchaSiteKey;
            document.body.appendChild(script);
        };
        loadReCaptcha();
    }, []);

    // useEffect(() => {
    //     debugger;
    //     if (selectedCreditCard) {
    //         //setShowSemafoneInput(true);
    //     }
    // }, [semaExpiryWatch]);

    useEffect(() => {
        register('cvvEnabled', {});
        setValue('cvvEnabled', window.cvvCheckEnabled, { shouldValidate: true });
        if (selectedCreditCard) {
            setCreditCardType(selectedCreditCard.Type);

            // const expiryMonth =
            //     selectedCreditCard.DisplayExpirationMonth.length == 1
            //         ? '0' + selectedCreditCard.DisplayExpirationMonth
            //         : selectedCreditCard.DisplayExpirationMonth;
            // setCreditCardExpiryMonth(expiryMonth);

            // const expiryYear =
            //     selectedCreditCard.DisplayExpirationYear.length == 4
            //         ? selectedCreditCard.DisplayExpirationYear.substr(2, 2)
            //         : '20' + selectedCreditCard.DisplayExpirationYear;
            // setCreditCardExpiryYear(expiryYear);

            // if (selectedCreditCard.Type == TmCreditCardType.TSC) {
            //     setTscCreditCardNumber(selectedCreditCard.MaskedNumber);
            //     setPaymentCardType(TmCreditCardSelectionType.TSC);
            //     setIsTscCreditCard(true);
            // } else {
            //     setShowSemafoneInput(false);
            // }
        }
        let semafoneConfig: TmSemafoneConfig;
        const getSemafoneData = async () => {
            const response = await getSemafoneConfig();
            semafoneConfig = response.data;
            PCILanguage = 'en_CA';
            PCIResetParams(semafoneConfig);
            PCIResetIframe();
        };
        getSemafoneData();
    }, []);

    const onSubmit = (data) => {
        //prepare payload for api call
        const creditCard = new TmCreditCardRequest();
        if (completeSemafoneResp) {
            const semafoneResp = JSON.parse(completeSemafoneResp);

            // check cvv
            if (cvvEnabled) {
                if (semafoneResp && semafoneResp.CardScheme == 'AMEX') {
                    if (!data.semacvv || data.semacvv.length != 4) {
                        setError(
                            'semacvv',
                            {
                                type: 'custom',
                                message:
                                    'We are unable to verify your credit card because of an invalid CVV number. Please try again.',
                            },
                            { shouldFocus: true },
                        );
                        return;
                    }
                }
                if (
                    (semafoneResp && semafoneResp.CardScheme == 'VISA') ||
                    (semafoneResp && semafoneResp.CardScheme == 'MASTERCARD')
                ) {
                    if (!data.semacvv || data.semacvv.length != 3) {
                        setError(
                            'semacvv',
                            {
                                type: 'custom',
                                message:
                                    'We are unable to verify your credit card because of an invalid CVV number. Please try again.',
                            },
                            { shouldFocus: true },
                        );
                        return;
                    }
                }
            }
            if (semafoneResp && semafoneResp.SemafoneValidationState == 'SUCCESS' && semafoneResp.Token) {
                creditCard.Type = AccountUtils.getCardTypeFromScheme(semafoneResp.CardScheme);
                creditCard.Number = semafoneResp.Token;
                creditCard.MaskedNumber = semafoneResp.MaskedPan;
                const parts = data.semaexpirynumber.split('/');
                const ccMonth = parts[0];
                const ccYear = parts[1];
                creditCard.DisplayExpirationMonth = ccMonth.length == 1 ? '0' + ccMonth : ccMonth;
                creditCard.DisplayExpirationYear = '20' + ccYear;
                creditCard.IsDefault = data.makedefault;
                if (cvvEnabled) {
                    creditCard.CVV = data.semacvv;
                }
                const cookie = new Cookie();
                if (cookie.TscClientIP) {
                    creditCard.ClientIP = cookie.TscClientIP;
                }
            } else if (semafoneResp.SemafoneValidationState == 'FAIL') {
                setError(
                    'semacvv',
                    {
                        type: 'custom',
                        message:
                            'We are unable to verify your credit card because of an invalid CVV number. Please try again.',
                    },
                    { shouldFocus: true },
                );
                setFormError(semafoneResp.SemafoneValidationErrorDesc as string);
                return;
            }
        }

        grecaptcha.ready(function () {
            grecaptcha
                .execute(reCaptchaSiteKey, {
                    action: selectedCreditCard == null ? 'addcreditcard' : 'updatecreditcard',
                })
                .then((token) => {
                    //recaptcha token added to payload
                    creditCard.ReCaptchaToken = token;
                    const payload = { creditcard: creditCard };
                    if (selectedCreditCard == null) {
                        // adding new card
                        mutation.mutate(payload, {
                            onSuccess: () => {
                                setMode('view');
                                updateAccordionAtIndex(0, true);
                                updateAccordionAtIndex(1, false);
                                setShowWarning(false);
                                setShowNotification(true);
                            },
                            onError: (error) => {
                                setFormError(error as string);
                                setValue('semacvv', '');
                                // setError('root.serverError', {
                                //     type: error as string,
                                //     message: error as string,
                                // });
                            },
                        });
                    } else {
                        //updating existing card
                        payload.creditcard['Id'] = selectedCreditCard.Id;
                        payload['Id'] = selectedCreditCard.Id;
                        //delete payload.creditcard.Expired;
                        //delete payload.creditcard.ExpirationDate;
                        updateMutation.mutate(payload, {
                            onSuccess: () => {
                                setMode('view');
                                updateAccordionAtIndex(0, true);
                                updateAccordionAtIndex(1, false);
                                setShowWarning(false);
                                setShowNotification(true);
                            },
                            onError: (error) => {
                                setFormError(error as string);
                                setValue('semacvv', '');
                                // setError('root.serverError', {
                                //     type: error as string,
                                //     message: error as string,
                                // });
                            },
                        });
                    }
                });
        });
    };
    return (
        <>
            <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
                {/* ---------------------------------------- */}
                {/* masked semafone input                    */}
                {/* ---------------------------------------- */}
                <div className="mb-4">
                    {selectedCreditCard && (
                        <>
                            <Controller
                                name="semacardnumber"
                                control={control}
                                defaultValue={selectedCreditCard?.MaskedNumber || ''}
                                rules={{ required: 'Number is required' }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <div
                                            className="tscform__form-wrap semaWrap"
                                            style={
                                                showSemafoneInput ? { display: 'none' } : { display: 'inline-block' }
                                            }
                                        >
                                            <MaskedInput
                                                mask={[
                                                    /[1-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                    /[0-9,*]/,
                                                ]}
                                                {...field}
                                                data-empty={semaCardWatch === '' ? 'true' : 'false'}
                                                data-error={getFieldState('semacardnumber').error ? 'true' : 'false'}
                                                className="tscform__form-input semaInput rounded-none text-base"
                                                guide={false}
                                                style={showSemafoneInput ? { display: 'none' } : { display: 'inline' }}
                                                onChange={onEditNonTscCreditCard}
                                                onClick={onEditNonTscCreditCard}
                                                tabIndex={0}
                                                type="tel"
                                                data-qm-block="true"
                                            />
                                            <label className="tscform__form-label semaLabel" htmlFor="tsccardnumber">
                                                Card Number*
                                            </label>
                                            {fieldState.error && (
                                                <div className="tscform__error-wrap">{fieldState.error.message}</div>
                                            )}
                                        </div>
                                    </>
                                )}
                            />
                            {creditCardType && !showSemafoneInput && (
                                <div className="card__verify-wrap">
                                    <div className="card__logo--verify">
                                        {creditCardType == TmCreditCardType.Visa && (
                                            <img
                                                src="/App_Assets/public/visa.svg"
                                                width="24"
                                                height="24"
                                                alt="visa-icon"
                                                style={{ marginLeft: '10px', marginTop: '-6px' }}
                                                className="tagCCImageVisa"
                                            ></img>
                                        )}
                                        {creditCardType == TmCreditCardType.MasterCard && (
                                            <img
                                                src="/App_Assets/public/mastercard.svg"
                                                alt="mastercard-icon"
                                                width="30"
                                                height="30"
                                                style={{
                                                    maxWidth: '84px',
                                                    width: '84px',
                                                    marginTop: '-4px',
                                                    marginLeft: '-7px',
                                                }}
                                                className="tagCCImageMC"
                                            ></img>
                                        )}
                                        {creditCardType == TmCreditCardType.Amex && (
                                            <img
                                                src="/App_Assets/public/amex.svg"
                                                alt="amex-icon"
                                                width="24"
                                                height="24"
                                                style={{ marginLeft: '5px', marginTop: '-6px' }}
                                                className="tagCCImageAmex"
                                            ></img>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ---------------------------------------- */}
                    {/* semafone input                           */}
                    {/* ---------------------------------------- */}
                    <div style={showSemafoneInput || !selectedCreditCard ? { display: 'block' } : { display: 'none' }}>
                        <div
                            style={{
                                display: 'flex',
                                marginTop: '15px',
                                marginBottom: '-10px',
                                flexDirection: 'column',
                            }}
                        >
                            <h3
                                className="card__card-number"
                                style={
                                    showSemafoneInput || !selectedCreditCard
                                        ? { display: 'inline-block' }
                                        : { display: 'none' }
                                }
                            >
                                Card Number*
                            </h3>
                            <div style={{ display: 'flex' }}>
                                <div className="semafoneCapturePage" id="semafoneCheckoutCapturePage" data-qm-block="true"></div>

                                {creditCardType && (
                                    <div className="card__verify-wrap">
                                        <div className="card__logo--verify">
                                            {creditCardType == TmCreditCardType.Visa && (
                                                <img
                                                    src="/App_Assets/public/visa.svg"
                                                    width="24"
                                                    height="24"
                                                    alt="visa-icon"
                                                    style={{ marginLeft: '10px' }}
                                                    className="tagCCImageVisa"
                                                ></img>
                                            )}
                                            {creditCardType == TmCreditCardType.MasterCard && (
                                                <img
                                                    src="/App_Assets/public/mastercard.svg"
                                                    alt="mastercard-icon"
                                                    width="30"
                                                    height="30"
                                                    style={{ maxWidth: '84px', width: '84px', marginTop: '-4px' }}
                                                    className="tagCCImageMC"
                                                ></img>
                                            )}
                                            {creditCardType == TmCreditCardType.Amex && (
                                                <img
                                                    src="/App_Assets/public/amex.svg"
                                                    alt="amex-icon"
                                                    style={{ paddingLeft: '5px' }}
                                                    width="24"
                                                    height="24"
                                                    className="tagCCImageAmex"
                                                ></img>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div>
                        <span id="semafoneResponseSpan" className="semafoneNoResponse"></span>
                    </div>
                    <div id="semaLoadingMsg"></div>
                    <div className="semafoneCCNumber" data-qm-block="true">
                        <input
                            type="text"
                            id="isPanFocusedOnInitialise"
                            defaultValue="false"
                            style={{ display: 'none' }}
                        />
                        <input
                            type="text"
                            id="isPanResetOnInitialFocus"
                            defaultValue="true"
                            style={{ display: 'none' }}
                        />
                        <input type="hidden" id="cardScheme" defaultValue="" />
                        <input type="hidden" id="maskedCardNumber" defaultValue="" />
                        <input type="hidden" id="token" defaultValue="" />
                        <input type="hidden" id="semafoneResponse" defaultValue="" />
                        <input
                            type="text"
                            id="completeSemafoneResponse"
                            value={completeSemafoneResp}
                            onChange={onNonTscCreditCardAdded}
                            style={{ display: 'none' }}
                        />
                    </div>
                    {/* creditcard__row--wrap */}
                    <div className="mt-4 grid grid-cols-1 gap-6 md2:grid-cols-2 " style={{ paddingTop: 0 }}>
                        {/* ---------------------------------------- */}
                        {/* expiry date                              */}
                        {/* ---------------------------------------- */}
                        <div className="creditcard__item">
                            <div className="creditcard__expirey-wrap">
                                <Controller
                                    name="semaexpirynumber"
                                    control={control}
                                    //defaultValue={selectedCreditCard?.MaskedNumber || ''}
                                    rules={{ required: 'Number is required' }}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <div className="tscform__form-wrap">
                                                <MaskedInput
                                                    mask={[/[0-9]/, /\d/, '/', /\d/, /\d/]}
                                                    {...field}
                                                    data-empty={semaExpiryWatch === '' ? 'true' : 'false'}
                                                    data-error={
                                                        getFieldState('semaexpirynumber').error ? 'true' : 'false'
                                                    }
                                                    className="tscform__form-input rounded-none text-base"
                                                    guide={false}
                                                    onChange={(event) => {
                                                        field.onChange(event);
                                                        if (errors.semaexpirynumber) {
                                                            clearErrors('semaexpirynumber');
                                                        }
                                                    }}
                                                    tabIndex={0}
                                                    type="tel"
                                                    data-qm-block="true"
                                                />
                                                <label className="tscform__form-label" htmlFor="semaexpirynumber">
                                                    Expiration date* (MM/YY)
                                                </label>
                                                {fieldState.error && (
                                                    <div className="tscform__error-wrap">
                                                        {fieldState.error.message}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                />
                            </div>
                        </div>

                        {/* ---------------------------------------- */}
                        {/*         cvv                              */}
                        {/* ---------------------------------------- */}
                        <div className="creditcard__item">
                            <div
                                className="creditcard__expirey-wrap"
                                style={{ display: window.cvvCheckEnabled ? 'block' : 'none' }}
                            >
                                <Controller
                                    name="semacvv"
                                    control={control}
                                    //defaultValue={selectedCreditCard?.MaskedNumber || ''}
                                    rules={{ required: 'Number is required' }}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <div className="tscform__form-wrap">
                                                <MaskedInput
                                                    mask={[/[0-9]/, /\d/, /\d/, /\d/]}
                                                    {...field}
                                                    data-empty={semaCvvWatch === '' ? 'true' : 'false'}
                                                    data-error={getFieldState('semacvv').error ? 'true' : 'false'}
                                                    className="tscform__form-input rounded-none text-base"
                                                    guide={false}
                                                    onChange={(event) => {
                                                        field.onChange(event);
                                                        if (errors.semacvv) {
                                                            clearErrors('semacvv');
                                                        }
                                                    }}
                                                    tabIndex={0}
                                                    type="tel"
                                                    data-qm-block="true"
                                                />
                                                <label className="tscform__form-label" htmlFor="semacvv">
                                                    Security code*
                                                </label>
                                                <Popover placement="left-start">
                                                    <PopoverTrigger
                                                        className="popover__button"
                                                        style={{
                                                            position: 'absolute',
                                                            top: '7px',
                                                            right: '0',
                                                            zIndex: '2',
                                                        }}
                                                    >
                                                        <span className="visually-hidden">CVV tooltip</span>
                                                        <InfoTooltipSvg className="popover__tooltip" />
                                                    </PopoverTrigger>
                                                    <PopoverContent className="popover__message">
                                                        3 digit number on the back of your VISA or MASTERCARD
                                                        <br />
                                                        4 digit number on the front of your American Express Card
                                                        <PopoverClose />
                                                        <PopoverArrow className="popover__arrow" />
                                                    </PopoverContent>
                                                </Popover>
                                                {fieldState.error && (
                                                    <div className="tscform__error-wrap">
                                                        {fieldState.error.message}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ---------------------------------------- */}
                    {/* Make creditcard default checkbox         */}
                    {/* ---------------------------------------- */}

                    <div className="tscform__checkbox--wrap mb-5 mt-7 flex items-center">
                        <Controller
                            name="makedefault"
                            control={control}
                            render={({ field, fieldState }) => (
                                <>
                                    <Checkbox.Root
                                        checked={field.value}
                                        //defaultChecked={selectedCreditCard?.IsDefault}
                                        onCheckedChange={field.onChange}
                                        className={`tscform__checkbox peer flex h-6 w-6 cursor-pointer border border-solid border-black disabled:cursor-default data-[state=checked]:bg-black data-[state=checked]:disabled:border-[#a3a3a3] data-[state=checked]:disabled:bg-[#a3a3a3]`}
                                        id="c2"
                                    ></Checkbox.Root>
                                </>
                            )}
                        />
                        <label
                            className="mb-0 cursor-pointer select-none pl-2.5 text-[13px] font-semibold leading-4 peer-disabled:cursor-default"
                            htmlFor="c2"
                        >
                            Make this card my default payment card
                        </label>
                    </div>
                </div>
                {/* --------- submit --------- */}
                {/* creditcard__buttons--container */}
                <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                    <Button
                        type="button"
                        aria-label={'Cancel changes'}
                        className="bg-white"
                        size="lg"
                        variant="outline"
                        // className="creditcard__buttons cancel"
                        tabIndex={0}
                        onClick={() => {
                            if (selectedCreditCard != null) {
                                //edit mode
                                setMode('view');
                                updateAccordionAtIndex(0, true);
                                updateAccordionAtIndex(1, false);
                            } else {
                                //add mode
                                setMode('view');
                                updateAccordionAtIndex(0, true);
                                updateAccordionAtIndex(1, false);
                            }
                        }}
                    >
                        {selectedCreditCard ? 'Cancel changes' : 'Cancel'}
                    </Button>
                    <Button
                        type="submit"
                        aria-label={'Save changes'}
                        // className="creditcard__buttons save"
                        disabled={!isValid || isSubmitting}
                        tabIndex={!isValid ? -1 : 0}
                        size="lg"
                    >
                        {selectedCreditCard ? 'Save changes' : 'Save card'}
                    </Button>
                </div>
            </form>
            {mutation.isLoading || isMutating ? (
                <div className="blockPageWrap" style={{ display: 'block' }}>
                    <div className="blockPageOverlay"></div>
                    <div className="blockMsg">
                        <LoadingSvg />
                    </div>
                </div>
            ) : null}
            {/* <DevTool control={control} /> */}
        </>
    );
};
